import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Theme {
  name: string;
  displayName: string;
  isDark?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<string>('filterlabs');
  public currentTheme$ = this.currentThemeSubject.asObservable();

  // Available themes based on your styles.css configuration
  public readonly availableThemes: Theme[] = [
    { name: 'filterlabs', displayName: 'FilterLabs Light', isDark: false },
    { name: 'filterlabs-dark', displayName: 'FilterLabs Dark', isDark: true },
    { name: 'light', displayName: 'Light', isDark: false },
    { name: 'dark', displayName: 'Dark', isDark: true },
    { name: 'cupcake', displayName: 'Cupcake', isDark: false }
  ];

  constructor() {
    this.initializeTheme();
  }

  private initializeTheme(): void {
    try {
      // Get saved theme from localStorage or use default
      const savedTheme = localStorage.getItem('selected-theme') || 'filterlabs';
      this.setTheme(savedTheme);
    } catch (error) {
      console.error('Error initializing theme:', error);
      // Fallback to default theme
      this.setTheme('filterlabs');
    }
  }

  public setTheme(themeName: string): void {
    try {
      // Try to use themeChange function if available
      if (typeof window.themeChange === 'function') {
        window.themeChange(themeName);
      } else {
        // Fallback: manually set data-theme attribute
        document.documentElement.setAttribute('data-theme', themeName);
      }
      
      this.currentThemeSubject.next(themeName);
      localStorage.setItem('selected-theme', themeName);
    } catch (error) {
      console.error('Error changing theme:', error);
      // Ensure fallback works
      document.documentElement.setAttribute('data-theme', themeName);
      this.currentThemeSubject.next(themeName);
      localStorage.setItem('selected-theme', themeName);
    }
  }

  public getCurrentTheme(): string {
    return this.currentThemeSubject.value;
  }

  public toggleDarkMode(): void {
    const currentTheme = this.getCurrentTheme();
    const isCurrentlyDark = this.availableThemes.find(t => t.name === currentTheme)?.isDark;
    
    if (isCurrentlyDark) {
      this.setTheme('filterlabs');
    } else {
      this.setTheme('filterlabs-dark');
    }
  }

  public getThemeByName(themeName: string): Theme | undefined {
    return this.availableThemes.find(theme => theme.name === themeName);
  }

  public getDarkThemes(): Theme[] {
    return this.availableThemes.filter(theme => theme.isDark);
  }

  public getLightThemes(): Theme[] {
    return this.availableThemes.filter(theme => !theme.isDark);
  }

  // Cleanup method for component destruction
  public destroy(): void {
    this.currentThemeSubject.complete();
  }
} 